/**
 * CyberSpinner Component
 * A cyberpunk-styled loading spinner with rotating colors
 * Supports variable speed for idle/active states and celebration animation on success
 */

import { Box } from '@mui/material';

interface CyberSpinnerProps {
  size?: number;  // Size in pixels
  speed?: 'slow' | 'fast' | 'rapid';  // Rotation speed: slow (5s) for idle, fast (1s) for active, rapid (0.4s) for searching
  celebrateSuccess?: boolean;  // Trigger celebration pulse animation
}

export function CyberSpinner({ size = 48, speed = 'fast', celebrateSuccess = false }: CyberSpinnerProps) {
  const borderWidth = Math.max(2, size / 16);
  const innerOffset = Math.max(4, size / 12);
  const innerOffset2 = innerOffset * 2;
  
  // Rotation duration based on speed prop
  const spinDuration = speed === 'slow' ? '5s' : speed === 'rapid' ? '0.4s' : '1s';
  const spinDuration2 = speed === 'slow' ? '7.5s' : speed === 'rapid' ? '0.6s' : '1.5s';
  const spinDuration3 = speed === 'slow' ? '4s' : speed === 'rapid' ? '0.3s' : '0.8s';

  return (
    <Box sx={{ 
      position: 'relative', 
      width: size, 
      height: size,
      // Celebration pulse animation on success
      ...(celebrateSuccess && {
        '@keyframes celebrate-pulse': {
          '0%': { 
            transform: 'scale(1)',
            filter: 'drop-shadow(0 0 20px rgba(34, 197, 94, 0.6))'
          },
          '50%': { 
            transform: 'scale(1.15)',
            filter: 'drop-shadow(0 0 40px rgba(34, 197, 94, 0.9)) drop-shadow(0 0 60px rgba(34, 197, 94, 0.5))'
          },
          '100%': { 
            transform: 'scale(1)',
            filter: 'drop-shadow(0 0 20px rgba(34, 197, 94, 0.6))'
          }
        },
        animation: 'celebrate-pulse 0.6s ease-in-out'
      })
    }}>
      <style>
        {`
          @keyframes cyber-spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes cyber-color-cycle {
            0% { border-top-color: #60a5fa; box-shadow: 0 0 ${size/4}px #60a5fa40; }
            25% { border-top-color: #34d399; box-shadow: 0 0 ${size/4}px #34d39940; }
            50% { border-top-color: #fbbf24; box-shadow: 0 0 ${size/4}px #fbbf2440; }
            75% { border-top-color: #f472b6; box-shadow: 0 0 ${size/4}px #f472b640; }
            100% { border-top-color: #60a5fa; box-shadow: 0 0 ${size/4}px #60a5fa40; }
          }
          @keyframes cyber-color-cycle-success {
            0%, 100% { border-top-color: #22c55e; box-shadow: 0 0 ${size/4}px #22c55e60; }
            50% { border-top-color: #34d399; box-shadow: 0 0 ${size/3}px #34d39980; }
          }
        `}
      </style>
      {/* Outer ring */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          borderRadius: '50%',
          border: `${borderWidth}px solid rgba(96, 165, 250, 0.2)`,
          borderTopColor: celebrateSuccess ? '#22c55e' : '#60a5fa',
          animation: celebrateSuccess 
            ? `cyber-spin ${spinDuration} linear infinite, cyber-color-cycle-success 1s ease-in-out infinite`
            : `cyber-spin ${spinDuration} linear infinite, cyber-color-cycle 3s ease-in-out infinite`,
        }}
      />
      {/* Middle ring */}
      <Box
        sx={{
          position: 'absolute',
          top: innerOffset,
          left: innerOffset,
          width: `calc(100% - ${innerOffset * 2}px)`,
          height: `calc(100% - ${innerOffset * 2}px)`,
          borderRadius: '50%',
          border: `${borderWidth * 0.75}px solid rgba(251, 191, 36, 0.2)`,
          borderBottomColor: celebrateSuccess ? '#34d399' : '#fbbf24',
          animation: `cyber-spin ${spinDuration2} linear infinite reverse`,
        }}
      />
      {/* Inner ring (only for larger spinners) */}
      {size >= 32 && (
        <Box
          sx={{
            position: 'absolute',
            top: innerOffset2,
            left: innerOffset2,
            width: `calc(100% - ${innerOffset2 * 2}px)`,
            height: `calc(100% - ${innerOffset2 * 2}px)`,
            borderRadius: '50%',
            border: `${borderWidth * 0.5}px solid rgba(52, 211, 153, 0.2)`,
            borderTopColor: '#34d399',
            animation: `cyber-spin ${spinDuration3} linear infinite`,
          }}
        />
      )}
    </Box>
  );
}

export default CyberSpinner;
